import React, { useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { deviceAPI } from '../../features/devices/deviceAPI';
import '../Devices/devices.css';
import './productiondevices.css';

const formatDate = (value) => {
  return value ? new Date(value).toLocaleString() : '—';
};

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const ProductionDevices = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Date filter
  const [dateFilter, setDateFilter] = useState('today');

  const [fromDate, setFromDate] = useState(
    formatInputDate(new Date())
  );

  const [toDate, setToDate] = useState(
    formatInputDate(new Date())
  );

  // --------------------------------------------------
  // LOAD PRODUCTION RECORDS
  // --------------------------------------------------

  const loadRecords = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await deviceAPI.getAllProductionTracking();

      const sortedData = [...data].sort(
        (a, b) =>
          new Date(b.created_date || 0) -
          new Date(a.created_date || 0)
      );

      setRecords(sortedData);
    } catch (requestError) {
      setError(
        requestError.message ||
          'Unable to load production devices.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  // --------------------------------------------------
  // DATE FILTER
  // --------------------------------------------------

  const handleDateFilter = (filter) => {
    setDateFilter(filter);

    const today = new Date();

    let from;
    let to;

    // ---------------- TODAY ----------------
    if (filter === 'today') {
      from = new Date(today);
      to = new Date(today);
    }

    // ---------------- YESTERDAY ----------------
    else if (filter === 'yesterday') {
      from = new Date(today);
      from.setDate(from.getDate() - 1);

      to = new Date(from);
    }

    // ---------------- WEEKLY ----------------
    else if (filter === 'weekly') {
      from = new Date(today);
      from.setDate(from.getDate() - 6);

      to = new Date(today);
    }

    // ---------------- MONTHLY ----------------
    else if (filter === 'monthly') {
      from = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );

      to = new Date(today);
    }

    // ---------------- CUSTOM ----------------
    else if (filter === 'custom') {
      // Clear both dates.
      setFromDate('');
      setToDate('');

      return;
    }

    setFromDate(formatInputDate(from));
    setToDate(formatInputDate(to));
  };

  // --------------------------------------------------
  // FILTER RECORDS
  // --------------------------------------------------

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();

    // If custom range has no dates yet
    if (!fromDate || !toDate) {
      return [];
    }

    const start = new Date(`${fromDate}T00:00:00`);
    const end = new Date(`${toDate}T23:59:59.999`);

    return records.filter((record) => {
      const createdDate = new Date(record.created_date);

      // Date condition
      const matchesDate =
        !isNaN(createdDate.getTime()) &&
        createdDate >= start &&
        createdDate <= end;

      if (!matchesDate) {
        return false;
      }

      // Search condition
      if (!term) {
        return true;
      }

      return [
        record.handset,
        record.controlid,
        record.status,
        record.registedname,
        record.registeredname,
        record.observations
      ].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(term)
      );
    });
  }, [
    records,
    search,
    fromDate,
    toDate
  ]);

  const downloadRecords = useMemo(() => {
  if (!fromDate || !toDate) {
    return [];
  }

  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T23:59:59.999`);

  return records.filter((record) => {
    const createdDate = new Date(record.created_date);

    return (
      !isNaN(createdDate.getTime()) &&
      createdDate >= start &&
      createdDate <= end
    );
  });
}, [records, fromDate, toDate]);
  // --------------------------------------------------
  // EXCEL DOWNLOAD
  // --------------------------------------------------

  const downloadExcel = async () => {
    // -----------------------------------------------
    // CUSTOM DATE VALIDATION
    // -----------------------------------------------

    if (dateFilter === 'custom') {
      if (!fromDate) {
        alert('Please select FROM date.');
        return;
      }

      if (!toDate) {
        alert('Please select TO date.');
        return;
      }

      if (toDate < fromDate) {
        alert('TO date cannot be before FROM date.');
        return;
      }
    }

    // -----------------------------------------------
    // NO RECORDS
    // -----------------------------------------------

    if (downloadRecords.length === 0) {
      alert('No records available to download.');
      return;
    }

    // -----------------------------------------------
    // CREATE WORKBOOK
    // -----------------------------------------------

    const workbook = new ExcelJS.Workbook();

    const worksheet =
      workbook.addWorksheet('Production Devices');

    // -----------------------------------------------
    // EXCEL COLUMNS
    // -----------------------------------------------

    worksheet.columns = [
      {
        header: 'Handset',
        key: 'handset',
        width: 25
      },
      {
        header: 'Control ID',
        key: 'controlid',
        width: 30
      },
      {
        header: 'Status',
        key: 'status',
        width: 20
      },
      {
        header: 'Testing Person',
        key: 'testingPerson',
        width: 25
      },
      {
        header: 'Observations',
        key: 'observations',
        width: 40
      },
      {
        header: 'Created Time',
        key: 'created_date',
        width: 25
      }
    ];

    // -----------------------------------------------
    // HEADER STYLE
    // -----------------------------------------------

    const headerRow = worksheet.getRow(1);

    headerRow.height = 28;

    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: {
          argb: 'FFFFFF'
        }
      };

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: '2563EB'
        }
      };

      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle'
      };

      cell.border = {
        top: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        },
        bottom: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        },
        left: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        },
        right: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        }
      };
    });

    // -----------------------------------------------
    // ADD DATA
    // -----------------------------------------------

    downloadRecords.forEach((record) => {
      const excelStatus = record.status || 'Unknown';

      const row = worksheet.addRow({
        handset: record.handset || '—',

        controlid:
          record.controlid || '—',

        status: excelStatus,

        testingPerson:
          record.registedname ||
          record.registeredname ||
          '—',

        observations:
          record.observations || '—',

        created_date:
          record.created_date
            ? formatDate(record.created_date)
            : '—'
      });

      // -------------------------------------------
      // GENERAL CELL STYLE
      // -------------------------------------------

      row.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          wrapText: true
        };

        cell.border = {
          top: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          },
          bottom: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          },
          left: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          },
          right: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          }
        };
      });

      // -------------------------------------------
      // STATUS COLOR
      // -------------------------------------------

      const statusCell = row.getCell('status');

      const status =
        String(record.status || '').trim();

      let backgroundColor = 'E5E7EB';
      let textColor = '374151';

      // REGISTERED = RED
      if (status === 'Registered') {
        backgroundColor = 'FECACA';
        textColor = 'B91C1C';
      }

      // UNDER TESTING = ORANGE
      else if (status === 'UnderTesting') {
        backgroundColor = 'FED7AA';
        textColor = 'C2410C';
      }

      // TEST COMPLETED = GREEN
      else if (status === 'Testcompleted') {
        backgroundColor = 'BBF7D0';
        textColor = '15803D';
      }

      // INSTALLED = BLUE
      else if (status === 'Installed') {
        backgroundColor = 'BFDBFE';
        textColor = '1D4ED8';
      }

      statusCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: backgroundColor
        }
      };

      statusCell.font = {
        bold: true,
        color: {
          argb: textColor
        }
      };

      statusCell.alignment = {
        horizontal: 'center',
        vertical: 'middle'
      };
    });

    // -----------------------------------------------
    // FREEZE HEADER
    // -----------------------------------------------

    worksheet.views = [
      {
        state: 'frozen',
        ySplit: 1
      }
    ];

    // -----------------------------------------------
    // EXCEL FILTER
    // -----------------------------------------------

    worksheet.autoFilter = {
      from: 'A1',
      to: 'F1'
    };

    // -----------------------------------------------
    // FILE NAME
    // -----------------------------------------------

    let fileName = 'Production_Devices';

    if (dateFilter === 'custom') {
      fileName += `_${fromDate}_to_${toDate}`;
    } else {
      fileName += `_${dateFilter}`;
    }

    fileName += '.xlsx';

    // -----------------------------------------------
    // DOWNLOAD
    // -----------------------------------------------

    const buffer =
      await workbook.xlsx.writeBuffer();

    const blob = new Blob(
      [buffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    );

    const url =
      window.URL.createObjectURL(blob);

    const link =
      document.createElement('a');

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <div className="devices-app">
      <div className="devices-card">

        {/* HEADER */}
        <div className="devices-header">
          <div>
            <h1 className="devices-title">
              Production Devices
            </h1>

            <p className="devices-subtitle">
              Production tracking and testing activity
            </p>
          </div>

          <button
            className="device-pairs-btn production-tracking-btn"
            onClick={loadRecords}
          >
            Refresh
          </button>
        </div>

        {/* DATE FILTER */}
        <div className="production-filters">

          {/* DATE BUTTONS */}
          <div className="production-date-buttons">

            <button
              className={
                dateFilter === 'today'
                  ? 'active'
                  : ''
              }
              onClick={() =>
                handleDateFilter('today')
              }
            >
              Today
            </button>

            <button
              className={
                dateFilter === 'yesterday'
                  ? 'active'
                  : ''
              }
              onClick={() =>
                handleDateFilter('yesterday')
              }
            >
              Yesterday
            </button>

            <button
              className={
                dateFilter === 'weekly'
                  ? 'active'
                  : ''
              }
              onClick={() =>
                handleDateFilter('weekly')
              }
            >
              Weekly
            </button>

            <button
              className={
                dateFilter === 'monthly'
                  ? 'active'
                  : ''
              }
              onClick={() =>
                handleDateFilter('monthly')
              }
            >
              Monthly
            </button>

            <button
              className={
                dateFilter === 'custom'
                  ? 'active'
                  : ''
              }
              onClick={() =>
                handleDateFilter('custom')
              }
            >
              Custom Range
            </button>

          </div>

          {/* FROM / TO / SEARCH / DOWNLOAD */}
          <div className="production-date-range">

            {/* FROM */}
            <div className="production-date-field">

              <label>
                FROM
              </label>

              <input
                type="date"
                value={fromDate}
                onChange={(event) => {
                  const selectedFrom =
                    event.target.value;

                  setDateFilter('custom');

                  setFromDate(selectedFrom);

                  // If existing TO is before
                  // the new FROM, clear TO.
                  if (
                    toDate &&
                    selectedFrom > toDate
                  ) {
                    setToDate('');
                  }
                }}
              />

            </div>

            {/* TO */}
            <div className="production-date-field">

              <label>
                TO
              </label>

              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                disabled={!fromDate}
                onChange={(event) => {
                  setDateFilter('custom');

                  setToDate(
                    event.target.value
                  );
                }}
              />

            </div>

            {/* SEARCH */}
            <div className="production-search-wrapper">

              <input
                className="search-input"
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Search ..."
              />

            </div>

            {/* DOWNLOAD */}
            <button
              className="production-download-btn"
              onClick={downloadExcel}
            >
              Download
            </button>

          </div>
        </div>

        {/* CONTENT */}
        {loading ? (
          <div className="empty-state">
            <p>
              Loading production devices…
            </p>
          </div>
        ) : error ? (
          <div className="error-container">

            <p className="error-message">
              {error}
            </p>

            <button
              className="retry-btn"
              onClick={loadRecords}
            >
              Retry
            </button>

          </div>
        ) : (
          <div className="table-container">

            <table className="devices-table production-devices-table">

              <thead>
                <tr>
                  <th>Handset</th>
                  <th>Control ID</th>
                  <th>Status</th>
                  <th>Testing Person</th>
                  <th>Observations</th>
                  <th>Created Time</th>
                </tr>
              </thead>

              <tbody>

                {filteredRecords.map((record) => (

                  <tr
                    key={
                      record.id ||
                      `${record.handset}-${record.created_date}`
                    }
                  >

                    <td className="handset-cell">
                      {record.handset || '—'}
                    </td>

                    <td className="device-id-cell">
                      {record.controlid || '—'}
                    </td>

                    <td>
                      <span
                        className={`production-status production-status-${String(
                          record.status || ''
                        ).toLowerCase()}`}
                      >
                        {record.status ===
                        'Testcompleted'
                          ? 'Test Completed'
                          : record.status ===
                            'UnderTesting'
                          ? 'Under Testing'
                          : record.status ||
                            'Unknown'}
                      </span>
                    </td>

                    <td>
                      <span className="production-person-name">
                        {record.registedname ||
                          record.registeredname ||
                          '—'}
                      </span>
                    </td>

                    <td>
                      {record.observations ||
                        '—'}
                    </td>

                    <td>
                      <time className="production-created-time">
                        {formatDate(
                          record.created_date
                        )}
                      </time>
                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

            {filteredRecords.length === 0 && (
              <div className="empty-state">
                <p>
                  No production tracking
                  records found.
                </p>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

export default ProductionDevices;
